import { createContext, type ReactNode, useContext, useMemo } from "react";

import { locales, nav, ui } from "../../docs.config.ts";
import { type Label, type LocaleOption } from "../../types/content.ts";

/** As chaves da interface, derivadas do idioma base — não uma lista à parte. */
export type UiKey = keyof (typeof ui)["pt"];

interface LocaleValue {
  locale: string;
  t: (key: UiKey, params?: Record<string, unknown>) => string;
  label: (value: Label | null | undefined) => string;
  tagLabel: (tag: string) => string;
  available: LocaleOption[];
}

// tagLabels vive na entrada de nav do tipo "openapi".
const TAG_LABELS: Record<string, Record<string, string> | undefined> =
  nav.find((n) => n.type === "openapi")?.tagLabels ?? {};

const UI: Record<string, Record<string, string> | undefined> = ui;

const LocaleContext = createContext<LocaleValue | null>(null);

const localeCodes: string[] = locales.available.map((l) => l.code);
export const isLocale = (code: string | undefined): code is string => code !== undefined && localeCodes.includes(code);

export function LocaleProvider({ locale, children }: { locale: string; children: ReactNode }) {
  const value = useMemo<LocaleValue>(() => {
    const strings = UI[locale] ?? UI[locales.fallback] ?? {};
    const fallbackStrings = UI[locales.fallback] ?? {};

    // t("search") → string da interface. Cai no idioma de fallback quando a
    // chave existe só lá, para que uma tradução faltando vire texto em
    // português em vez de sumir da tela.
    //
    // t("lines", { n: 42 }) preenche os {placeholders} da string. Interpolar em
    // vez de concatenar deixa cada idioma livre para ordenar a frase do seu
    // jeito ("42 linhas" / "42 lines" / "42 líneas" cabem, mas nem sempre o
    // número vem antes).
    const t = (key: UiKey, params?: Record<string, unknown>): string => {
      const value = strings[key] ?? fallbackStrings[key] ?? key;
      if (!params) return value;
      return value.replace(/\{(\w+)\}/g, (match, name: string) =>
        params[name] === undefined ? match : String(params[name])
      );
    };

    // Rótulos de docs.config aceitam string ("MCP") ou objeto por idioma
    // ({ pt: "Começar", en: "Get started" }).
    const label = (value: Label | null | undefined): string => {
      if (value == null) return "";
      if (typeof value === "string") return value;
      return value[locale] ?? value[locales.fallback] ?? Object.values(value)[0] ?? "";
    };

    // Nome cru da tag do OpenAPI → rótulo exibido.
    //
    // Sem entrada para o idioma, cai direto no nome do spec — e NÃO no idioma de
    // fallback como o resto das strings. O spec é escrito em inglês, então o
    // nome cru já é uma tradução válida para EN e um fallback razoável para ES;
    // passar pelo português mostraria "Autenticação" para um leitor inglês.
    // É também por isso que o mapa `en` em docs.config só lista as tags que
    // precisam de ajuste de grafia ("OtaSite" → "OTA Site").
    const tagLabel = (tag: string): string => TAG_LABELS[locale]?.[tag] ?? tag;

    return { locale, t, label, tagLabel, available: locales.available };
  }, [locale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

/**
 * Só o código do idioma, e sem exigir o provider.
 *
 * `aplicarTokens` roda dentro de CodeBlock, que também é montado fora da árvore
 * de uma página (pré-visualizações, testes). Fazer um bloco de código explodir
 * por falta de contexto seria pior do que cair no idioma padrão.
 */
export function useLocaleCode(): string {
  return useContext(LocaleContext)?.locale ?? locales.default;
}

export function useLocale(): LocaleValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale precisa estar dentro de <LocaleProvider>.");
  return ctx;
}
